import { InfinityIcon } from "@phosphor-icons/react";
import { ComponentProps, ReactNode } from "react";
import { Control, FieldPath, FieldValues, Path } from "react-hook-form";
import {
  inputClassName as baseInputClassName,
  formInputClassName,
  Svg,
  SvgName,
} from "../../..";
import { cn } from "../../../utils";
import { Input, InputProps } from "../Input";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "./Form";

export type FormInputProps<TForm extends FieldValues> = {
  control: Control<TForm>;
  name: Path<TForm>;
  label?: ReactNode;
  hasMessage?: boolean;
  messageClassName?: string;
  className?: string;
  inputClassName?: string;
  unit?: string;
  errorRing?: boolean;
  svg?: {
    name: SvgName;
    alt: string;
    isColored?: boolean;
    className?: string;
  };
  Icon?: ReactNode;
  showInfiniteButton?: boolean;
  onInfiniteClick?: () => void;
  transformValue?: (value: string) => string;
} & InputProps;

export function FormInput<TForm extends FieldValues>({
  control,
  name,
  ...props
}: FormInputProps<TForm>) {
  return (
    <FormField
      control={control}
      name={name}
      render={(renderProps) => <FormInputField {...props} {...renderProps} />}
    />
  );
}

type RenderProps<
  TForm extends FieldValues = FieldValues,
  TName extends FieldPath<TForm> = FieldPath<TForm>,
> = Parameters<ComponentProps<typeof FormField<TForm, TName>>["render"]>[0];

type FormInputFieldProps<
  TForm extends FieldValues,
  TName extends FieldPath<TForm> = FieldPath<TForm>,
> = Omit<FormInputProps<TForm>, "control" | "name"> & RenderProps<TForm, TName>;

function FormInputField<
  TForm extends FieldValues,
  TName extends FieldPath<TForm> = FieldPath<TForm>,
>({
  label,
  hasMessage = false,
  messageClassName,
  className,
  inputClassName,
  unit,
  errorRing = true,
  svg,
  Icon,
  type = "text",
  showInfiniteButton = false,
  onInfiniteClick,
  transformValue,
  field,
  fieldState,
  ...props
}: FormInputFieldProps<TForm, TName>) {
  const { onChange, disabled } = props;

  const handleInfiniteClick = () => {
    onInfiniteClick?.();
  };

  const numberOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const stringValue = transformValue
      ? transformValue(e.target.value)
      : e.target.value;
    if (stringValue === "") {
      field.onChange("");
      return;
    }
    const value = Number(stringValue);
    if (isNaN(value)) {
      return;
    }
    field.onChange(value);
    if (onChange) {
      onChange(e);
    }
  };

  const stringOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const transformedValue = transformValue
      ? transformValue(e.target.value)
      : e.target.value;
    field.onChange(transformedValue);
    if (onChange) {
      onChange(e);
    }
  };

  return (
    <FormItem className={cn(formInputClassName, className)}>
      {label ? <FormLabel>{label}</FormLabel> : null}

      <FormControl>
        <div className="relative">
          {svg && (
            <Svg
              name={svg.name}
              alt={svg.alt}
              className={cn(
                "absolute inset-y-0 left-2 h-full w-5",
                svg.className
              )}
              isColored={svg.isColored}
            />
          )}
          {Icon && (
            <div className="pointer-events-none absolute inset-y-0 left-2 flex h-full w-5 items-center">
              {Icon}
            </div>
          )}
          {showInfiniteButton && (
            <button
              type="button"
              onClick={handleInfiniteClick}
              className="absolute inset-y-0 right-2 flex items-center justify-center px-2"
              aria-label="Set to Infinity"
            >
              <InfinityIcon size={16} />
            </button>
          )}
          {unit ? (
            <div className="flex items-center">
              <Input
                {...field}
                {...props}
                {...(type === "number"
                  ? {
                      onChange: numberOnChange,
                    }
                  : {
                      onChange: stringOnChange,
                    })}
                type={type}
                className={cn(
                  baseInputClassName,
                  "rounded-r-none",
                  { "pl-8": svg || Icon },
                  inputClassName,
                  {
                    "ring-destructive focus-visible:ring-destructive focus-visible:ring-offset-background ring-1 focus-visible:ring-offset-1":
                      fieldState.error && errorRing,
                    "cursor-not-allowed bg-gray-100 text-gray-500": disabled,
                  }
                )}
              />
              <span
                className={cn(
                  "bg-gray-25 h-full content-center rounded-r-md border border-l-0 border-gray-300 px-3 py-[7px] text-sm text-gray-700",
                  {
                    "ring-destructive focus-visible:ring-destructive focus-visible:ring-offset-background ring-1 focus-visible:ring-offset-1":
                      fieldState.error && errorRing,
                    "cursor-not-allowed bg-gray-100 text-gray-500": disabled,
                  }
                )}
              >
                {unit}
              </span>
            </div>
          ) : (
            <Input
              {...field}
              value={field.value ?? ""}
              {...props}
              {...(type === "number"
                ? {
                    onChange: numberOnChange,
                  }
                : {
                    onChange: stringOnChange,
                  })}
              type={type}
              className={cn(
                baseInputClassName,
                { "pl-8": svg || Icon },
                inputClassName,
                {
                  "ring-destructive focus-visible:ring-destructive focus-visible:ring-offset-background ring-1 focus-visible:ring-offset-1":
                    fieldState.error && errorRing,
                  "cursor-not-allowed bg-gray-100 text-gray-500": disabled,
                }
              )}
            />
          )}
        </div>
      </FormControl>

      {hasMessage ? <FormMessage className={messageClassName} /> : null}
    </FormItem>
  );
}

export default FormInput;
